import { ArrowUpward, FormatQuoteRounded } from "@mui/icons-material";
import { Box, Button, Divider, Stack, Typography } from "@mui/material";
import { styled } from "@mui/material/styles";

import type { ColorName } from "~/components/charts";

export const InsightBox = styled(Stack)(({ theme }) => ({
  backgroundColor: theme.palette.support[50],
  borderRadius: 12,
  padding: `${theme.spacing(4)} ${theme.spacing(6)}`,
  borderLeft: `2px solid ${theme.palette.primary[300]}`,
  width: "720px",
  gap: 2,
  marginLeft: "70px",
  position: "relative",
  // TODO: check this box shadow with UX
  boxShadow: "2px 2px 4px 0 rgba(233, 234, 255, 0.44)",
}));

export const CommentImage = styled("img")(({ theme }) => ({
  width: "88px",
  height: "88px",
  position: "absolute",
  left: "-70px",
  top: "16.5px",
  borderRadius: "12px",
  boxShadow: theme.shadows[4],
}));

/** @deprecated Import from `@monthly/ui` instead
 * @example
 * import { FooterSectionProps } from '@monthly/ui' */
export interface FooterSectionProps {
  elementId: string;
  imageUrl?: string;
  insightText?: string;
  insightAuthor?: string;
  showInsight?: boolean;
  insightAuthorColor?: ColorName;
}

/** @deprecated Import from `@monthly/ui` instead
 * @example
 * import { FooterSection } from '@monthly/ui' */
export function FooterSection({
  elementId,
  imageUrl,
  insightText,
  insightAuthor,
  insightAuthorColor,
  showInsight = true,
}: FooterSectionProps) {
  const handleGoToTop = (elementId?: string) => () => {
    if (elementId) {
      const element = document.getElementById(elementId);
      if (element) {
        element.scrollIntoView({ behavior: "smooth", block: "start" });
        return;
      }
    }
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  return (
    <Stack alignItems="center" gap={8} mt={4}>
      <Stack
        direction="row"
        alignItems="center"
        justifyContent="center"
        gap={2}
      >
        <Divider
          sx={(theme) => ({
            borderColor: theme.palette.secondary[400],
            width: "40px",
          })}
        />
        <Typography
          variant="caption"
          sx={(theme) => ({
            fontWeight: theme.typography.fontWeightMedium,
            color: theme.palette.secondary[400],
          })}
        >
          Has llegado al final de este tablero
        </Typography>
        <Divider
          sx={(theme) => ({
            borderColor: theme.palette.secondary[400],
            width: "40px",
          })}
        />
      </Stack>

      {/* Insight Box */}
      {showInsight && (
        <InsightBox>
          {imageUrl && (
            <Stack>
              <CommentImage
                sx={(theme) => ({
                  backgroundColor: insightAuthorColor
                    ? theme.palette[insightAuthorColor].main
                    : theme.palette.support[50],
                })}
                src={imageUrl}
                alt={
                  insightAuthor
                    ? `Foto de ${insightAuthor}`
                    : "Foto del autor de las cita"
                }
              />
            </Stack>
          )}
          <Stack direction="row">
            <FormatQuoteRounded
              sx={(theme) => ({
                color: theme.palette.primary.main,
                fontSize: 24,
                transform: "scaleX(-1)",
              })}
            />
            <Stack gap={2}>
              <Typography
                variant="h4"
                sx={(theme) => ({
                  color: theme.palette.secondary[600],
                  fontWeight: theme.typography.fontWeightSemiBold,
                })}
              >
                {insightText}"
              </Typography>
              <Typography variant="caption" color="primary.main">
                {insightAuthor}
              </Typography>
            </Stack>
          </Stack>
        </InsightBox>
      )}

      {/* Back to Top Button */}
      <Stack alignItems="center">
        <Button
          variant="text"
          startIcon={<ArrowUpward sx={{ fontSize: 18 }} />}
          onClick={handleGoToTop(elementId)}
        >
          Volver arriba
        </Button>
      </Stack>
    </Stack>
  );
}
